import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useMemo } from "react";
import { FixNumber } from "../../../../common/orders";
import { getCurrencyShorthand } from "../../../../providers/StationProvider";
import SimpleTable from "../../../common/tables/SimpleTable";
import { ToggleSurface } from "../../../common/core/Surface";
import { Grid } from "@mui/material";
import { MissionEnums, OrderEnums } from "../../../../common/constants";
import { Compare } from "@emberly/zenith-client";
import { AllTasksCompleted, AssumeStorageState } from "../../../../common/mission";
import { useOrdersWithMissions } from "../../../../hooks/useOrdersWithMissions";
import { TimeView } from "../../../common/core/Time";

const ORDERS_QUERY = {
  path: "payment.invoice.state",
  comparer: Compare.EQ,
  value: OrderEnums.InvoiceState.None,
  name: "open_invoice_orders"
};

export default function OpenInvoiceOrdersSection() {
  const { t } = useTranslation();
  const { tabId } = useParams();
  const navigate = useNavigate();

  const { orders, showLoading } = useOrdersWithMissions(ORDERS_QUERY);

  const columns = useMemo(() => {
    return [
      {
        field: "number",
        type: "number",
        headerName: t("entity:order:number"),
        flex: 0
      },
      {
        field: "missionNumber",
        type: "number",
        headerName: t("openInvoiceOrders:fieldMission"),
        flex: 0
      },
      {
        field: "storage",
        headerName: t("openInvoiceOrders:fieldStorageState"),
        valueGetter: (_, row) => AssumeStorageState(row.mission),
        valueFormatter: (value) =>
          value === MissionEnums.StorageState.None ? t("openInvoiceOrders:noStorage") :
            value === MissionEnums.StorageState.Stored ?
              t("openInvoiceOrders:storageNotCompleted") :
              t("openInvoiceOrders:storageCompleted"),
      },
      {
        field: "taskState",
        type: "boolean",
        headerName: t("openInvoiceOrders:fieldTaskState"),
        valueGetter: (_, row) => AllTasksCompleted(row.mission),
        renderCell: (params) => params.value ? t("openInvoiceOrders:tasksCompleted") : t("openInvoiceOrders:tasksNotCompleted"),
        flex: 1,
        minWidth: 120
      },
      {
        field: "missionCreated",
        type: "date",
        headerName: t("openInvoiceOrders:fieldMissionCreated"),
        valueGetter: (_, row) => !!row.mission?.created ? new Date(row.mission?.created) : null,
        renderCell: (params) => <TimeView time={params.row.mission?.created} />,
        flex: 1,
        minWidth: 120
      },
      {
        field: "customer.name",
        headerName: t("entity:order:customer"),
        valueGetter: (_, row) => row.customer?.name || "-",
        minWidth: 160,
        flex: 0.5,
      },
      {
        field: "mission.actors.requester.name",
        headerName: t("common:tables:requester"),
        valueGetter: (_, row) => row.mission?.actors.ownerIsNotRequester ? row.mission?.actors.requester.name : row.mission?.actors.owner.name,
        flex: 1,
        minWidth: 120,
      },
      {
        field: "mission.target.carDetails.registration",
        headerName: t("entity:mission:target:registration"),
        valueGetter: (_, row) => {
          const type = row.mission?.target.type;
          const target = type === 1 ? row.mission?.target?.carDetails : (type === 2 ? row.mission?.target?.boatDetails : null);
          return target?.registration || "-";
        },
        minWidth: 100,
        flex: 0.5
      },
      {
        field: "payment.calculatedTotal.value",
        type: "number",
        entityType: "decimal",
        headerName: t("entity:order:payment:calculatedTotal"),
        valueGetter: (_, row) => row.payment.calculatedTotal?.value || "0",
        valueFormatter: (value) => FixNumber(value),
        renderCell: (params) => `${FixNumber(params.value)} ${getCurrencyShorthand(t, params.row.payment.calculatedTotal?.currency)}`,
        minWidth: 120
      },
      {
        field: "deductible.price.value",
        type: "number",
        entityType: "decimal",
        headerName: t("entity:order:deductible"),
        valueGetter: (_, row) => Number(row.deductible?.price?.value || "0"),
        valueFormatter: (value) => FixNumber(value),
        renderCell: (params) => params.row.deductible ? `${FixNumber(params.row.deductible?.price?.value)} ${getCurrencyShorthand(t, params.row.deductible?.price?.currency)}` : "-",
        minWidth: 120
      },
      {
        field: "mission.actors.contract.agreement.name",
        headerName: t("entity:agreement:type"),
        valueGetter: (_, row) => row.mission?.actors?.contract?.agreement?.name || "-",
        minWidth: 160,
        flex: 0.5
      },
      {
        field: "comment",
        headerName: t("entity:order:internalComment"),
        valueGetter: (_, row) => row.comment || "-",
        minWidth: 160,
        flex: 0.5
      },
    ]
  }, [t]);

  const onRowClick = useCallback((params, ev) => {
    navigate(!tabId ? `./ready/${params.row.missionId}/${params.id}` : `./${params.row.missionId}/${params.id}`, { relative: "path" });
  }, [navigate, tabId]);

  return (
    <>
      <ToggleSurface>
        <Grid item xs={12}>
          <SimpleTable
            tableId="openinvoice_orders"
            onRowClick={onRowClick}
            columns={columns}
            rows={orders}
            loading={showLoading}
            emptyStateTitle={t("openInvoiceOrders:emptyStateTitle")}
            emptyStateDescription={t("openInvoiceOrders:emptyStateDescription")}
            xs={12}
          />
        </Grid>
      </ToggleSurface>
    </>
  );
}

